import useGetLineReuseInfo from '@/hooks/api/useCallAPIGetLineReuseInfo';
import useGetContractInfo from '@/hooks/api/useGetContractInfo';
import useGetDocumentsPostingInfo from '@/hooks/api/useGetDocumentsPostingInfo';
import useGetInstallationAddressInfo from '@/hooks/api/useGetInstallationAddressInfo';
import useGetPromotionSetting from '@/hooks/api/useGetPromotionSetting';
import useSetLoading from '@/hooks/useSetLoading';
import alertState from '@/states/alertState';
import { CustomerInfoState } from '@/states/customerInfoState';
import { LineReuseInfoState, LineReuseInfoType } from '@/states/lineReuseInfoState';
import { LoginInfo, LoginInfoState } from '@/states/loginInfoState';
import promotionState from '@/states/promotionState';
import { ContractInfoResponse, ContractInfoType } from '@/types/api/mypage/contractInfo';
import { DocumentsPostingInfoResponse } from '@/types/api/mypage/documentsPostingInfo';
import { InstallationAddressInfoResponse } from '@/types/api/mypage/installationAddressInfo';
import { LinereuseInfoResponse } from '@/types/api/mypage/lineReuseInfo';
import { Promotion } from '@/types/api/mypage/promotionInfo';
import { isExpiredToken } from '@/utils/cognito';
import { formatDate, formatZipCode } from '@/utils/commonFunc';
import url from '@/utils/config';
import { ApiUrl, Constants } from '@/utils/constants';
import { AxiosResponse } from 'axios';
import Router from 'next/router';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import AccordionButton from '../atoms/AccordionButton';
import Button from '../atoms/Button';
import Heading from '../atoms/Heading';
import LinkButton from '../atoms/LinkButton';

type Props = {
  anchor: string;
};

const CustomerInfo = ({ anchor }: Props) => {
  const [customerInfo, setCustomerInfo] = useRecoilState<ContractInfoType>(CustomerInfoState);
  const [lineReuseInfo, setLineReuseInfo] = useRecoilState<LineReuseInfoType>(LineReuseInfoState);
  const promotionInfo = useRecoilValue(promotionState);
  const loginInfo = useRecoilValue<LoginInfo>(LoginInfoState);
  const { execute: getContractInfo } = useGetContractInfo();
  const { execute: getPromotionInfo } = useGetPromotionSetting();
  const setAlert = useSetRecoilState(alertState);
  const { startLoading, finishLoading } = useSetLoading();
  const [isInstallationAddressDisplay, setIsInstallationAddressDisplay] = useState(false);
  const { execute: getInstallationAddressInfo } = useGetInstallationAddressInfo();
  const [installationAddressInfo, setInstallationAddressInfo] =
    useState<InstallationAddressInfoResponse>({
      zip_code: '',
      prefecture: '',
      city: '',
      town: '',
      address_optional: '',
      town_for_entry: '',
      optional: '',
      build_name: '',
      room_number: '',
      addr_key: '',
      cn_type: '',
      build_id: '',
    });
  // 書面投函日API
  const { execute: getDocumentsPostingInfo } = useGetDocumentsPostingInfo();
  // キャリアチェンジ情報取得APIAPI
  const { execute: getLineReuseInfo } = useGetLineReuseInfo();
  // 書面投函日state
  const [documentsPostingInfo, setDocumentsPostingInfo] = useState<DocumentsPostingInfoResponse[]>(
    []
  );
  // 書面投函日トグル
  const [isDocumentsPostingDisplay, setIsDocumentsPostingDisplay] = useState(false);

  useEffect((): void => {
    callAPIGetContractInfo();
    getPromotionInfo();
    callAPIGetDocumentsPostingInfo();
    callAPIGetLinereuseInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 契約者情報取得API呼び出し
  const callAPIGetContractInfo = async () => {
    try {
      startLoading(ApiUrl.Read.CONTRACT_INFO);
      const { data }: AxiosResponse<ContractInfoResponse, any> = await getContractInfo();
      // 取得した値をセット
      setCustomerInfo({
        ...customerInfo,
        contract_no: data.contract_no,
        last_name: data.last_name,
        first_name: data.first_name,
        last_name_kana: data.last_name_kana,
        first_name_kana: data.first_name_kana,
        zip_code: formatZipCode(data.zip_code),
        prefecture: data.prefecture,
        city: data.city,
        town: data.town,
        address_optional: data.address_optional,
        tel: data.tel,
        tel_daytime: data.tel_daytime,
        email: data.email,
      });
    } catch (error: any) {
      if (isExpiredToken(error)) {
        sessionStorage.clear();
        window.location.href = url('/login');
        return;
      }
      setAlert({
        isOpen: true,
        apiType: Constants.ApiType.API_GET,
      });
    } finally {
      finishLoading(ApiUrl.Read.CONTRACT_INFO);
    }
  };

  // 書面投函日情報取得API呼び出し
  const callAPIGetDocumentsPostingInfo = async () => {
    try {
      startLoading(ApiUrl.Read.DOCUMENTS_POSTING_INFO);
      const { data }: AxiosResponse<DocumentsPostingInfoResponse[], any> =
        await getDocumentsPostingInfo();

      if (data && Array.isArray(data) && data.length) {
        // 発送済みと完了だけ表示
        const filterData = data.filter((d) => {
          const stateComp: Constants.DocumentsPostingInfo.LM_STATE[] = [
            Constants.DocumentsPostingInfo.LM_STATE.COMPLETE,
            Constants.DocumentsPostingInfo.LM_STATE.SHIPPED,
          ];
          return stateComp.includes(d.lm_state);
        });
        setDocumentsPostingInfo(filterData);
        return;
      }

      setDocumentsPostingInfo([]);
    } catch (error: any) {
      if (isExpiredToken(error)) {
        sessionStorage.clear();
        window.location.href = url('/login');
        return;
      }
      setAlert({
        isOpen: true,
        apiType: Constants.ApiType.API_GET,
      });
    } finally {
      finishLoading(ApiUrl.Read.DOCUMENTS_POSTING_INFO);
    }
  };

  // キャリアチェンジ情報取得API呼び出し
  const callAPIGetLinereuseInfo = async () => {
    try {
      startLoading(ApiUrl.Read.LINE_REUSE_INFO);
      const { data }: AxiosResponse<LinereuseInfoResponse, any> = await getLineReuseInfo();
      setLineReuseInfo({
        ...lineReuseInfo,
        approval_in_num: data.approval_in_num,
        approval_out_num: data.approval_out_num,
        approval_out_num_limit_date: data.approval_out_num_limit_date,
        line_equip_type: data.line_equip_type,
        approval_out_num_limit_over_flg: data.approval_out_num_limit_over_flg,
      });
    } catch (error: any) {
      if (isExpiredToken(error)) {
        sessionStorage.clear();
        window.location.href = url('/login');
        return;
      }
      setAlert({
        isOpen: true,
        apiType: Constants.ApiType.API_GET,
      });
    } finally {
      finishLoading(ApiUrl.Read.LINE_REUSE_INFO);
    }
  };

  // プロモーション配信可否のメッセージを表示する
  const showPromotionMessage = (promotions: Promotion[], type: string): string => {
    const targetPromotion = promotions.find((promotion) => promotion.type === type);

    if (
      type === Constants.Promotion.Type.UP_SELL_MAIL ||
      type === Constants.Promotion.Type.BENEFIT_MAIL ||
      type === Constants.Promotion.Type.CROSS_SELL_MAIL
    ) {
      return targetPromotion?.is_enabled ? 'メールを受け取る' : 'メールを受け取らない';
    }
    return targetPromotion?.is_enabled ? 'SMSを受け取る' : 'SMSを受け取らない';
  };

  /** handle Click Contractor Button ハンドラ */
  const handleClickContractorButton = () => {
    Router.push('/contract/contractor');
  };

  // 設置先住所表示ボタン押下処理
  /** handle Click Installationg Address Display Button ハンドラ */
  const handleClickInstallationgAddressDisplayButton = async () => {
    if (!isInstallationAddressDisplay && installationAddressInfo.zip_code === '') {
      try {
        startLoading(ApiUrl.Read.INSTALLATION_ADDRESS_INFO);
        const { data }: AxiosResponse<InstallationAddressInfoResponse, any> =
          await getInstallationAddressInfo();
        setInstallationAddressInfo(data);
        setIsInstallationAddressDisplay(!isInstallationAddressDisplay);
      } catch (error: any) {
        if (isExpiredToken(error)) {
          sessionStorage.clear();
          window.location.href = url('/login');
          return;
        }
        setAlert({
          isOpen: true,
          apiType: Constants.ApiType.API_GET,
        });
      } finally {
        finishLoading(ApiUrl.Read.INSTALLATION_ADDRESS_INFO);
      }
    } else {
      setIsInstallationAddressDisplay(!isInstallationAddressDisplay);
    }
  };

  // お客さま情報ブロック1/2
  const jsxCustomer1 = (): React.JSX.Element => {
    return (
      <div>
        <Heading as='h3'>契約者情報</Heading>
        <div className='m-table'>
          <table>
            <tbody>
              <tr>
                <th className='m-table__th'>お客さま番号</th>
                <td className='m-table__td' data-testid='isp_cn_no'>
                  {customerInfo.contract_no}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>氏名</th>
                <td className='m-table__td' data-testid='name'>
                  {`${customerInfo.last_name} ${customerInfo.first_name}`}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>氏名(カナ)</th>
                <td className='m-table__td' data-testid='name_kana'>
                  {`${customerInfo.last_name_kana} ${customerInfo.first_name_kana}`}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>郵便番号</th>
                <td className='m-table__td' data-testid='address_zip_code'>
                  {customerInfo.zip_code}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>都道府県</th>
                <td className='m-table__td' data-testid='address_prefecture'>
                  {customerInfo.prefecture}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>市区町村</th>
                <td className='m-table__td' data-testid='address_city'>
                  {customerInfo.city}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>町名番地</th>
                <td className='m-table__td' data-testid='address_town'>
                  {customerInfo.town}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>マンション/ビル名・部屋番号</th>
                <td className='m-table__td' data-testid='address_optional'>
                  {customerInfo.address_optional}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>連絡先電話番号</th>
                <td className='m-table__td' data-testid='tel'>
                  {customerInfo.tel}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>日中の連絡先電話番号</th>
                <td className='m-table__td' data-testid='tel_daytime'>
                  {customerInfo.tel_daytime}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>メールアドレス</th>
                <td className='m-table__td' data-testid='mail'>
                  {customerInfo.email}
                </td>
              </tr>
              {lineReuseInfo.approval_in_num && (
                <tr>
                  <th className='m-table__th'>承諾番号(転入)</th>
                  <td className='m-table__td' data-testid='approval_in_num_test'>
                    {lineReuseInfo.approval_in_num}
                  </td>
                </tr>
              )}
              {lineReuseInfo.approval_out_num && (
                <tr>
                  <th className='m-table__th'>承諾番号(転出)</th>
                  <td className='m-table__td' data-testid='approval_out_num_test'>
                    {lineReuseInfo.approval_out_num}
                  </td>
                </tr>
              )}
              {lineReuseInfo.approval_out_num_limit_date && (
                <tr>
                  <th className='m-table__th'>承諾番号期限日(転出)</th>
                  <td className='m-table__td' data-testid='approval_out_num_limit_date_test'>
                    {formatDate(lineReuseInfo.approval_out_num_limit_date)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {lineReuseInfo.approval_out_num && lineReuseInfo.approval_out_num_limit_over_flg === '1' && (
            <div className='text-alert' data-testid='approval_out_num_alert_test'>
              承諾番号有効期限切れ。承諾番号（転出）をご利用の場合はサポートデスクへお問い合わせいただく必要があります。
            </div>
          )}
        </div>
      </div>
    );
  };

  // 設置先住所
  const jsxInstallationAddress = (): React.JSX.Element => {
    return (
      <div>
        <div className='accordion_container'>
          <Heading as='h3'>回線の設置先住所</Heading>
          <AccordionButton
            label={{ open: '表示', close: '非表示' }}
            isDisplay={isInstallationAddressDisplay}
            onClick={handleClickInstallationgAddressDisplayButton}
          />
        </div>
        {isInstallationAddressDisplay && (
          <div className='m-table'>
            <table>
              <tbody>
                <tr>
                  <th className='m-table__th'>郵便番号</th>
                  <td className='m-table__td' data-testid='installation_address_zip_code'>
                    {formatZipCode(installationAddressInfo.zip_code)}
                  </td>
                </tr>
                <tr>
                  <th className='m-table__th'>都道府県</th>
                  <td className='m-table__td' data-testid='installation_address_prefecture'>
                    {installationAddressInfo.prefecture}
                  </td>
                </tr>
                <tr>
                  <th className='m-table__th'>市区町村</th>
                  <td className='m-table__td' data-testid='installation_address_city'>
                    {installationAddressInfo.city}
                  </td>
                </tr>
                <tr>
                  <th className='m-table__th'>町名番地</th>
                  <td className='m-table__td' data-testid='installation_address_town'>
                    {installationAddressInfo.town}
                  </td>
                </tr>
                <tr>
                  <th className='m-table__th'>マンション/ビル名・部屋番号</th>
                  <td className='m-table__td' data-testid='installation_address_optional'>
                    {installationAddressInfo.address_optional}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // お客さま情報ブロック1/2の下部ボタン
  const jsxCustomer1Button = (): React.JSX.Element => {
    return (
      <div className='m-button col2'>
        <Button variant='secondary' onClick={handleClickContractorButton}>
          契約者情報の変更
        </Button>
        <LinkButton variant='secondary' href='/contract/mail'>
          メールアドレスの登録・変更
        </LinkButton>
      </div>
    );
  };

  // お客さま情報ブロック2/2
  const jsxCustomer2 = (): React.JSX.Element => {
    return (
      <div>
        <Heading as='h3'>ユーザーIDとパスワード</Heading>
        <div className='m-table'>
          <table>
            <tbody>
              <tr>
                <th className='m-table__th'>ユーザーID</th>
                <td className='m-table__td' data-testid='userId'>
                  {loginInfo.userId}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>パスワード</th>
                <td className='m-table__td' data-testid='password'>
                  安全のため表示されません
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // お客さま情報ブロック2/2下部ボタン
  const jsxCustomer2Button = (): React.JSX.Element => {
    return (
      <div className='m-button col2'>
        <LinkButton variant='secondary' href='/contract/id_password'>
          ユーザーID/パスワードの変更
        </LinkButton>
      </div>
    );
  };

  // プロモーション情報ブロック
  const jsxPromotion = (): React.JSX.Element => {
    return (
      <div>
        <Heading as='h3'>プロモーション情報配信</Heading>
        <div className='m-table'>
          <table>
            <tbody>
              <tr>
                <th className='m-table__th w-30'>
                  サービスに関するご案内［新サービス、新端末、キャンペーン等のご案内］
                </th>
                <td className='m-table__td' data-testid='upSellMail'>
                  {showPromotionMessage(promotionInfo, Constants.Promotion.Type.UP_SELL_MAIL)}
                </td>
                <td className='m-table__td' data-testid='upSellSms'>
                  {showPromotionMessage(promotionInfo, Constants.Promotion.Type.UP_SELL_SMS)}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>
                  優待特典に関するご案内を受け取る［NURO会員限定の特典（ソニーストアお買い物券、電子雑誌等）］
                </th>
                <td className='m-table__td' data-testid='benefitMail'>
                  {showPromotionMessage(promotionInfo, Constants.Promotion.Type.BENEFIT_MAIL)}
                </td>
                <td className='m-table__td' data-testid='benefitSms'>
                  {showPromotionMessage(promotionInfo, Constants.Promotion.Type.BENEFIT_SMS)}
                </td>
              </tr>
              <tr>
                <th className='m-table__th'>自社および提携会社の製品・サービスに関するご案内</th>
                <td className='m-table__td' data-testid='crossSellMail'>
                  {showPromotionMessage(promotionInfo, Constants.Promotion.Type.CROSS_SELL_MAIL)}
                </td>
                <td className='m-table__td' data-testid='crossSellSms'>
                  {showPromotionMessage(promotionInfo, Constants.Promotion.Type.CROSS_SELL_SMS)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // プロモーション情報ブロック下部ボタン
  const jsxPromotionButton = (): React.JSX.Element => {
    return (
      <div className='m-button col2'>
        <LinkButton variant='secondary' href='/contract/promotion'>
          プロモーション情報配信設定
        </LinkButton>
      </div>
    );
  };

  /** 書面投函日ブロック */
  const jsxDocumentsPostingInfo = (): React.JSX.Element | null => {
    // 配列が空なら表示しない
    if (documentsPostingInfo.length <= 0) return null;

    return (
      <div>
        <Heading as='h3'>書面発送履歴</Heading>
        <div className='m-table'>
          <table>
            <colgroup>
              <col width='50%' />
              <col width='50%' />
            </colgroup>
            <thead>
              <tr>
                <th className='m-table__th'>書面の種類</th>
                <th className='m-table__th'>書面発送完了日</th>
              </tr>
            </thead>
            <tbody data-testid='documents-posting-info'>
              {documentsPostingInfo.map((docPostInfo, index) => {
                // トグルを閉じている場合
                if (!isDocumentsPostingDisplay && index >= Constants.DocumentsPostingInfo.MAX)
                  return null;

                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={docPostInfo.lm_matter_type + index}>
                    <th className='m-table__th'>
                      {Constants.DocumentsPostingInfo.LM_MATTER_NAME[docPostInfo.lm_matter_type]}
                    </th>
                    <td className='m-table__td'>{formatDate(docPostInfo.lm_stat_upd_date)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* MAXより件数が多いときボタン表示 */}
          {documentsPostingInfo.length > Constants.DocumentsPostingInfo.MAX && (
            <AccordionButton
              label={{ open: '全件表示', close: '省略表示' }}
              isDisplay={isDocumentsPostingDisplay}
              onClick={() => {
                setIsDocumentsPostingDisplay(!isDocumentsPostingDisplay);
              }}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className='m-box-02' id={anchor}>
      <div className='m-box-02__inner'>
        {/** お客さま情報 */}
        <Heading as='h2' icon='customer'>
          お客さま情報
        </Heading>
        {/** お客様情報1 */}
        {jsxCustomer1()}
        {/** 設置先住所 */}
        {jsxInstallationAddress()}
        {jsxCustomer1Button()}
        {/** お客様情報2 */}
        {jsxCustomer2()}
        {jsxCustomer2Button()}
        {/** プロモーション情報 */}
        {jsxPromotion()}
        {jsxPromotionButton()}
        {/* 書面投函日 */}
        {jsxDocumentsPostingInfo()}
      </div>
    </div>
  );
};

export default CustomerInfo;
